import { createContext, ReactNode, useCallback, useMemo, useRef, useState } from "react";
import { MapboxDrawOptions } from "@mapbox/mapbox-gl-draw";
import * as Sentry from "@sentry/react";
import { Feature, FeatureCollection } from "geojson";
import { omit } from "lodash";

import { apiSlice, uploadVersionLayers } from "fond/api";
import { DRAWING_PROPERTIES_TO_EXCLUDE } from "fond/constants";
import { ToolbarConfig } from "fond/map/Toolbar";
import { loadProjectData } from "fond/project/redux";
import { UploadSources } from "fond/project/upload";
import { featureCollection } from "fond/turf";
import { LayerId } from "fond/types";
import { useAppDispatch, useAppSelector } from "fond/utils/hooks";

import MultiLayerEditHandler from "./MultiLayerEditHandler";

/**
 * Context for managing multi-layer editing state.
 * Provides reactive state for isSaving, isDirty, and edit operations.
 */
export const MultiLayerEditLegacyContext = createContext<{
  /**
   * The version ID being edited
   */
  versionId: string;
  /**
   * Drawing options including layers, validation, and snap settings
   */
  options: MapboxDrawOptions;
  /**
   * Toolbar configuration for the editing UI
   */
  config: ToolbarConfig;
  /**
   * Whether any layers are currently being saved/imported (reactive)
   */
  isSaving: boolean;
  /**
   * Whether all layer imports completed successfully (reactive)
   */
  allComplete: boolean;
  /**
   * Whether there are unsaved changes (reactive)
   */
  isDirty: boolean;
  /**
   * Set the dirty state
   */
  setIsDirty: (dirty: boolean) => void;
  /**
   * Get current features from draw control
   */
  getFeatures: () => Feature[];
  /**
   * Save all features across layers
   */
  save: () => Promise<unknown[]>;
  /**
   * Reset to initial features
   */
  reset: () => void;
  /**
   * Set the reset confirmation callback
   */
  setResetCallback: (callback: () => void) => void;
  /**
   * Set the getFeatures callback
   */
  setGetFeaturesCallback: (callback: () => Feature[]) => void;
  /**
   * Initial features to load (optional)
   */
  initialFeatures?: FeatureCollection;
  /**
   * Map of layer/sublayer ID to their associated Mapbox style layer IDs.
   * Used for efficiently toggling layer visibility.
   */
  layerStyleIds: Record<string, string[]> | undefined;
  /**
   * Set the layer style IDs mapping (called by MultiLayerEditHandler)
   */
  setLayerStyleIds: (styleIds: Record<string, string[]> | undefined) => void;
}>(undefined!);

interface MultiLayerEditProviderProps {
  children: ReactNode;
  versionId: string;
  options: MapboxDrawOptions;
  config: ToolbarConfig;
  initialFeatures?: FeatureCollection;
}

/**
 * Provider for multi-layer editing functionality.
 * Automatically renders MultiLayerEditHandler when layers are configured.
 *
 * Example:
 * ```tsx
 * <MultiLayerEditLegacyProvider versionId={versionId} options={editOptions} config={toolbarConfig}>
 *   <YourComponent />
 * </MultiLayerEditLegacyProvider>
 * ```
 */
export const MultiLayerEditLegacyProvider: React.FC<MultiLayerEditProviderProps> = ({
  children,
  versionId,
  options,
  config,
  initialFeatures,
}: MultiLayerEditProviderProps) => {
  const dispatch = useAppDispatch();
  const project = useAppSelector((state) => state.project.projects[state.project.projectId]?.project);
  const [isDirty, setIsDirty] = useState(false);
  const [isSavingState, setIsSavingState] = useState(false);
  const [saveSucceeded, setSaveSucceeded] = useState(false);
  const [layerStyleIds, setLayerStyleIds] = useState<Record<string, string[]> | undefined>(undefined);

  // Callbacks that will be set by MultiLayerEditHandler
  const resetCallbackRef = useRef<(() => void) | null>(null);
  const getFeaturesCallbackRef = useRef<(() => Feature[]) | null>(null);

  // Define the layer keys that we want to edit simultaneously
  // Exclude hidden layers from being saved
  const layerKeys = useMemo(() => options?.editOptions?.allLayers?.filter((layer) => !layer.Hide).map((layer) => layer.ID) || [], [options]);

  /**
   * Get current features from the draw control
   */
  const getFeatures = useCallback(() => {
    if (getFeaturesCallbackRef.current) {
      return getFeaturesCallbackRef.current();
    }
    return [];
  }, []);

  /**
   * Save all features across layers using legacy uploadVersionLayers
   */
  const save = useCallback(async () => {
    try {
      setIsSavingState(true);
      setSaveSucceeded(false);
      const features = getFeatures();

      // Build layers object with File for each layer
      const layers: Record<string, File[]> = {};

      layerKeys.forEach((layerKey) => {
        const layerFeatures = features.filter((f) => f.properties?.layerId === layerKey);

        // Strip drawing properties from features
        const cleanedFeatures = layerFeatures.map((f) => ({
          ...f,
          properties: omit(f.properties, DRAWING_PROPERTIES_TO_EXCLUDE),
        }));

        const fc = featureCollection(cleanedFeatures);
        layers[layerKey as LayerId] = [new File([JSON.stringify(fc)], `${layerKey}.geojson`)];
      });

      // Use uploadVersionLayers (legacy method) instead of importer
      await uploadVersionLayers({
        versionId,
        layers,
        source: UploadSources.edit,
        uploadedArea: null,
        convertToDualSided: false,
        xhrEvents: {
          onProgress: () => null,
          onUploadComplete: () => null,
          onComplete: async () => {
            // Invalidate cache to refresh the data
            dispatch(
              apiSlice.util.invalidateTags([
                { type: "Layers", id: versionId },
                { type: "VersionStatus", id: versionId },
                { type: "Version", id: versionId },
                { type: "FeatureTotals", id: versionId },
              ])
            );

            // Reload project data to update the map (for non-vector-tile projects)
            if (project) {
              await dispatch(loadProjectData(project));
            }

            setIsDirty(false);
            setSaveSucceeded(true);
            setIsSavingState(false);
          },
          onError: (error: unknown) => {
            setSaveSucceeded(false);
            setIsSavingState(false);
            Sentry.captureException(error);
          },
          onAbort: () => {
            setSaveSucceeded(false);
            setIsSavingState(false);
          },
        },
      });

      return [];
    } catch (error) {
      setSaveSucceeded(false);
      setIsSavingState(false);
      Sentry.captureException(error);
      return Promise.reject(error);
    }
  }, [dispatch, getFeatures, layerKeys, versionId, project]);

  /**
   * Reset to initial features
   */
  const reset = useCallback(() => {
    if (resetCallbackRef.current) {
      resetCallbackRef.current();
    }
  }, []);

  /**
   * Set the reset callback (called by MultiLayerEditHandler)
   */
  const setResetCallback = useCallback((callback: () => void) => {
    resetCallbackRef.current = callback;
  }, []);

  /**
   * Set the getFeatures callback (called by MultiLayerEditHandler)
   */
  const setGetFeaturesCallback = useCallback((callback: () => Feature[]) => {
    getFeaturesCallbackRef.current = callback;
  }, []);

  const value = useMemo(() => {
    // For legacy mode, we don't use import status - we track saving state locally
    const isSaving = isSavingState;

    // Only mark as complete when save actually succeeded (not on error/abort)
    const allComplete = !isSaving && saveSucceeded;

    return {
      versionId,
      options,
      config,
      isSaving,
      allComplete,
      isDirty,
      setIsDirty,
      getFeatures,
      save,
      reset,
      setResetCallback,
      setGetFeaturesCallback,
      initialFeatures,
      layerStyleIds,
      setLayerStyleIds,
    };
  }, [
    versionId,
    options,
    config,
    isSavingState,
    saveSucceeded,
    isDirty,
    getFeatures,
    save,
    reset,
    setResetCallback,
    setGetFeaturesCallback,
    initialFeatures,
    layerStyleIds,
  ]);

  // Check if there are layers configured for editing
  const hasLayersToEdit = options?.editOptions?.allLayers && options.editOptions.allLayers.length > 0;

  return (
    <MultiLayerEditLegacyContext.Provider value={value}>
      {children}
      {hasLayersToEdit && <MultiLayerEditHandler mode="legacy" />}
    </MultiLayerEditLegacyContext.Provider>
  );
};
